import { createContext, useCallback, useContext, useEffect, useState } from "react";

// Same key and logic as the pre-paint script in public/index.html; keep them in sync.
export const THEME_STORAGE_KEY = "botcircuits.theme";
export const THEMES = ["system", "light", "dark"];
const DEFAULT_THEME = "light";

const darkQuery = () => window.matchMedia?.("(prefers-color-scheme: dark)");

export function readTheme() {
    try {
        const saved = localStorage.getItem(THEME_STORAGE_KEY);
        return THEMES.includes(saved) ? saved : DEFAULT_THEME;
    } catch {
        return DEFAULT_THEME;
    }
}

const resolveDark = (theme) => theme === "dark" || (theme === "system" && !!darkQuery()?.matches);

const ThemeContext = createContext({ theme: DEFAULT_THEME, isDark: false, setTheme: () => {} });

export function ThemeProvider({ children }) {
    const [theme, setThemeState] = useState(readTheme);
    const [isDark, setIsDark] = useState(() => resolveDark(readTheme()));

    const setTheme = useCallback((next) => {
        try {
            localStorage.setItem(THEME_STORAGE_KEY, next);
        } catch {
            /* storage unavailable: the choice applies to this page only */
        }
        setThemeState(next);
    }, []);

    // Follow the OS setting in "system" mode, and choices made in other tabs.
    useEffect(() => {
        const media = darkQuery();
        const sync = () => {
            const current = readTheme();
            setThemeState(current);
            setIsDark(resolveDark(current));
        };
        media?.addEventListener("change", sync);
        window.addEventListener("storage", sync);
        return () => {
            media?.removeEventListener("change", sync);
            window.removeEventListener("storage", sync);
        };
    }, []);

    useEffect(() => {
        setIsDark(resolveDark(theme));
    }, [theme]);

    useEffect(() => {
        document.documentElement.classList.toggle("dark", isDark);
        document.querySelector('meta[name="theme-color"]')?.setAttribute("content", isDark ? "#181818" : "#ffffff");
    }, [isDark]);

    return <ThemeContext.Provider value={{ theme, isDark, setTheme }}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
